import difference from 'lodash/difference';
import flatMap from 'lodash/flatMap';

import {
  AgeRestriction,
  Category,
  DeliveryHandshakeRestriction,
  Item,
  Option,
  OptionBinding,
  OTCRestriction,
  Translation,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { UserAccessRight } from 'apis/restaurant-api/merchant-admin/user/types';
import { PosIntegrationStatus, Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { isRetailVenue } from 'common/utils';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { CountryConfig } from 'modules/country/types';

import { mandatoryWarningsForCountry } from './mandatoryWarnings';
import { MenuPermissions, MenuFeatures, MenuEditorElement, MenuEditorItem, MenuEditorOption } from './types';

export const memoizeWeak = <T extends {}, R>(fn: (obj: T) => R) => {
  const cache = new WeakMap<T, R>();
  return (obj: T) => {
    const cached = cache.get(obj);
    if (cached) {
      return cached;
    }
    const value = fn(obj);
    cache.set(obj, value);
    return value;
  };
};

export const mergeTranslations = (
  newPrimaryLanguageTranslation: Translation | null,
  currentTranslations: Translation[],
): Translation[] => {
  if (!newPrimaryLanguageTranslation) {
    return []; // Destroying all secondary language translations as well, if there were any
  }

  const currentPrimaryLanguageTranslation = currentTranslations.find(
    t => t.lang === newPrimaryLanguageTranslation.lang,
  );
  const isChanged =
    !currentPrimaryLanguageTranslation ||
    newPrimaryLanguageTranslation.value !== currentPrimaryLanguageTranslation.value;

  return [
    newPrimaryLanguageTranslation,
    ...currentTranslations
      .filter(translation => translation.lang !== newPrimaryLanguageTranslation.lang)
      .map(translation => ({ ...translation, verified: isChanged ? false : translation.verified })),
  ];
};

export const buildMenuPermissions = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
  venuePosIntegrationStatus?: PosIntegrationStatus | null,
): MenuPermissions => {
  return {
    catalogCreate: permissionUtils.hasCatalogCreatePermission(permissions),
    catalogRead: permissionUtils.hasCatalogReadPermission(permissions),
    categoryImages: permissionUtils.hasMenuCategoryImagesPermission(permissions, accessRights, venue),
    csvItemImport: permissionUtils.hasCsvItemImportPermission(permissions, accessRights, venue),
    csvItemUpdate: permissionUtils.hasCsvItemUpdatePermission(permissions, accessRights, venue),
    csvItemPriceUpdate: permissionUtils.hasCsvItemPriceUpdatePermission(permissions),
    customCommissionEdit: permissionUtils.hasMenuCustomCommissionEditPermission(permissions),
    customCommissionRead: permissionUtils.hasMenuCustomCommissionReadPermission(permissions),
    discount: permissionUtils.hasMenuDiscountPermission(permissions),
    contentEdit: permissionUtils.hasMenuContentEditPermission(
      permissions,
      accessRights,
      venue,
      venuePosIntegrationStatus,
    ),
    exportImport: permissionUtils.hasMenuExportImportPermission(
      permissions,
      accessRights,
      venue,
      venuePosIntegrationStatus,
    ),
    itemExportImport: permissionUtils.hasMenuItemExportImportPermission(permissions, accessRights, venue),
    itemVatChange: permissionUtils.hasMenuItemVATChangePermission(permissions, accessRights, venue),
    menuSync: permissionUtils.hasMenuSyncPermission(permissions, accessRights, venue, venuePosIntegrationStatus),
    posIdField: permissionUtils.hasPosIdFieldPermission(permissions, accessRights, venue),
    systemIdRead: permissionUtils.hasSystemIdReadPermission(permissions, accessRights, venue),
    woltSpecials: permissionUtils.hasWoltSpecialsViewPermission(permissions),
  };
};

export const buildMenuFeatures = ({
  venue,
  venueIsPosIntegrated,
  hasCatalogSource,
  countryConfig,
  restockingRulesEnabled,
}: {
  venue: Venue;
  venueIsPosIntegrated: boolean;
  hasCatalogSource: boolean;
  countryConfig: CountryConfig;
  restockingRulesEnabled: boolean;
}): MenuFeatures => {
  const isRetail = isRetailVenue(venue);

  return {
    isRetail,
    bottleDeposits: process.env.APP_ENV === 'production' ? !!countryConfig.bottle_deposits_enabled : true,
    bundleItems: venue.country === 'DEU',
    hasCatalogSource,
    posIntegration: venueIsPosIntegrated,
    inventoryManagement: venue.inventory_management_enabled,
    overTheCounter: isRetail && !!countryConfig.over_the_counter_allowed,
    overTheCounterHyperlinkAllowed: isRetail && !!countryConfig.over_the_counter_item_hyperlink_allowed,
    extendedUsageInstructions: venue.product_line === 'pharmacy',
    categoryImages: venue.menu_layout === 'categories',
    hasPickerEnabled: venue.picker_api_enabled,
    hasReturnPoliciesEnabled: venue.returns_enabled,
    hasWoltPlusSpecialsEnabled: venue.is_wolt_plus,
    supportWeightedItems: venue.product_line === 'grocery',
    mandatoryWarnings: !!mandatoryWarningsForCountry[venue.country],
    restockingRulesEnabled,
    externalInfoHyperlinkAllowed: !!countryConfig.item_external_info_hyperlink_allowed,
  };
};

const isOptionValueEnabled = (options: Option[], optionValueId: string) => {
  const allOptionValues = flatMap(options, option => option.values);
  const prerequisiteValue = allOptionValues.find(optionValue => optionValue.id === optionValueId);
  if (!prerequisiteValue) {
    throw new Error(`Option value with ID ${optionValueId} not found`);
  }
  return prerequisiteValue.enabled;
};

const optionValueBelongsToOptions = (options: Option[], optionValueId: string) => {
  return options.some(o => o.values.some(ov => ov.id === optionValueId));
};

const optionBindingCanBeVisible = (optionsBoundToItem: Option[], optionBinding: OptionBinding) => {
  return optionBinding.prerequisite_values.every(prerequisiteVelueId => {
    return (
      optionValueBelongsToOptions(optionsBoundToItem, prerequisiteVelueId) &&
      isOptionValueEnabled(optionsBoundToItem, prerequisiteVelueId)
    );
  });
};

export type OptionBindingPurchasabilityValidationError =
  | 'all_option_values_disabled'
  | 'too_many_disabled_option_values';

const validateOptionBinding = (
  optionsBoundToItem: Option[],
  optionBinding: OptionBinding,
): OptionBindingPurchasabilityValidationError[] => {
  if (!optionBindingCanBeVisible(optionsBoundToItem, optionBinding)) {
    return [];
  }

  const option = optionsBoundToItem.find(o => o.id === optionBinding.option_id);
  if (!option) {
    throw new Error(`Option with ID ${optionBinding.option_id} not found`);
  }

  if (option.type === 'multi_choice') {
    const multiChoiceConfig = optionBinding.multi_choice_config;
    if (!multiChoiceConfig) {
      throw new Error(`Multi-choice option binding with ID ${optionBinding.id} has no multi_choice_config`);
    }

    const numAvailableSelections = option.values.reduce((acc, optionValue) => {
      if (!optionValue.multi_choice_config) {
        throw new Error(`Multi-choice option value with ID ${optionValue.id} has no multi_choice_config`);
      }

      return optionValue.enabled
        ? acc + (optionValue.multi_choice_config.selection_range.max || multiChoiceConfig.max_single_selections)
        : acc;
    }, 0);
    return numAvailableSelections >= multiChoiceConfig.total_selections.min ? [] : ['too_many_disabled_option_values'];
  }

  return option.values.some(value => value.enabled) ? [] : ['all_option_values_disabled'];
};

const buildOptionIndexById = memoizeWeak((options: Option[]) => {
  const index: { [key: string]: Option } = {};
  for (let i = 0, l = options.length; i < l; i++) {
    const option = options[i];
    index[option.id] = option;
  }
  return index;
});

export const validateItemPurchasability = (
  options: Option[],
  item: Item,
): { optionBindingId: string; error: OptionBindingPurchasabilityValidationError }[] => {
  const optionIndex = buildOptionIndexById(options);
  const optionsBoundToItem = item.option_bindings.map(ob => {
    const option: Option | undefined = optionIndex[ob.option_id];
    if (!option) {
      throw new Error(`Option with ID ${ob.option_id} not found`);
    }
    return option;
  });
  return flatMap(item.option_bindings, optionBinding => {
    return validateOptionBinding(optionsBoundToItem, optionBinding).map(validationError => ({
      optionBindingId: optionBinding.id,
      error: validationError,
    }));
  });
};

export const getUnpurchasableItems = (items: Item[], options: Option[]): Item[] => {
  return items.filter(item => validateItemPurchasability(options, item).length > 0);
};

export const isMenuEditorItem = (el: MenuEditorElement): el is MenuEditorItem => el.type === 'item';

export const isMenuEditorOption = (el: MenuEditorElement): el is MenuEditorOption => el.type === 'option';

export const isAgeRestriction = (
  restriction: OTCRestriction | AgeRestriction | DeliveryHandshakeRestriction,
): restriction is AgeRestriction => restriction.type === 'age_restricted';

export const isOTCRestriction = (
  restriction: OTCRestriction | AgeRestriction | DeliveryHandshakeRestriction,
): restriction is OTCRestriction => restriction.type === 'over_the_counter';

export const flattenCategories = (categories: Category[]) =>
  categories.flatMap(category => [category, ...category.child_categories]);

export const getParentCategory = (categoryId: string, categories: Category[]) =>
  categories.find(category => category.child_categories.some(subcategory => subcategory.id === categoryId)) ?? null;

export const getItemsThatWillBecomeUnpurchasable = (
  simulatedOption: Option,
  allItems: Item[],
  allOptions: Option[],
) => {
  const currentUnpurchasableItems = getUnpurchasableItems(allItems, allOptions);
  const nextUnpurchasableItems = getUnpurchasableItems(
    allItems,
    allOptions.map(o => (o.id === simulatedOption.id ? simulatedOption : o)),
  );
  return difference(nextUnpurchasableItems, currentUnpurchasableItems);
};
